import { useState, Fragment } from "react";
import { Decimal } from "decimal.js";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@radix-ui/react-collapsible"
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Download } from "lucide-react";
import { TopLevelResult } from "@/lib/api/risk/profiles";
import { formatNumberToDisplay } from "@/lib/api/common";

interface RiskManagementSummaryTablesProps {
  data: TopLevelResult | undefined;
  isLoading?: boolean;
}

// Helper function to calculate variance percentage
const calculateVariance = (actual?: string, projected?: string): number => {
  if (!actual || !projected) return 0;

  const actualVal = new Decimal(actual);
  const projectedVal = new Decimal(projected);

  if (projectedVal.isZero()) return 0;
  if (!projectedVal.isZero() && actualVal.isZero()) return 0;

  return actualVal.minus(projectedVal).dividedBy(projectedVal).times(100).toNumber();
};

// Helper function to get variance display with color
const VarianceDisplay = ({ actual, projected }: { actual?: string; projected?: string }) => {
  if (!actual || !projected) return <span>—</span>;

  const variance = calculateVariance(actual, projected);
  const isPositive = variance >= 0;

  return (
    <span className={`font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
      {formatNumberToDisplay(variance, { maximumFractionDigits: 2, suffix: "%" })}
    </span>
  );
};

// Top level production summary card
const TotalProductionSummaryCard = ({ data }: { data: TopLevelResult }) => {
  if (!data?.summaries?.uom_summaries) return null;

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <CardTitle>Total Production Summary</CardTitle>
        <CardDescription>Overall production metrics across all UOMs</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Object.entries(data.summaries.uom_summaries).map(([uom, summary]) => (
            <Card key={uom} className="p-4 shadow-sm">
              <div className="text-lg font-semibold mb-2">{uom}</div>
              <div className="grid grid-cols-2 gap-y-2">
                <div className="text-sm text-muted-foreground">Projected:</div>
                <div className="text-sm font-medium">
                  {formatNumberToDisplay(summary.total_projected_production, { suffix: uom })}
                </div>
                <div className="text-sm text-muted-foreground">Actual:</div>
                <div className="text-sm font-medium">
                  {formatNumberToDisplay(summary.total_actual_production, { suffix: uom })}
                </div>
                <div className="text-sm text-muted-foreground">Variance:</div>
                <div>
                  <VarianceDisplay
                    actual={summary.total_actual_production}
                    projected={summary.total_projected_production}
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Yearly production breakdown table
const YearlyProductionTable = ({ data }: { data: TopLevelResult }) => {
  const [expandedUoms, setExpandedUoms] = useState<Record<string, boolean>>({});
  if (!data?.summaries?.uom_summaries) return null;

  const toggleUom = (uom: string) => {
    setExpandedUoms(prev => ({
      ...prev,
      [uom]: !prev[uom]
    }));
  };

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Yearly Production Breakdown</CardTitle>
            <CardDescription>Production totals by year for each UOM</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">UOM / Year</TableHead>
              <TableHead>Projected Production</TableHead>
              <TableHead>Actual Production</TableHead>
              <TableHead>Variance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(data.summaries.uom_summaries).map(([uom, summary]) => (
              <Fragment key={uom}>
                <TableRow className="bg-muted/50 cursor-pointer" onClick={() => toggleUom(uom)}>
                  <TableCell className="font-medium flex items-center">
                    {expandedUoms[uom] ? <ChevronUp className="h-4 w-4 mr-2" /> : <ChevronDown className="h-4 w-4 mr-2" />}
                    {uom}
                  </TableCell>
                  <TableCell>{formatNumberToDisplay(summary.total_projected_production, { suffix: uom })}</TableCell>
                  <TableCell>{formatNumberToDisplay(summary.total_actual_production, { suffix: uom })}</TableCell>
                  <TableCell>
                    <VarianceDisplay
                      actual={summary.total_actual_production}
                      projected={summary.total_projected_production}
                    />
                  </TableCell>
                </TableRow>

                {expandedUoms[uom] && Object.entries(summary.by_year).map(([year, yearData]) => (
                  <TableRow key={`${uom}-${year}`} className="bg-background/50">
                    <TableCell className="pl-8">{year}</TableCell>
                    <TableCell>{formatNumberToDisplay(yearData.total_projected_production, { suffix: uom })}</TableCell>
                    <TableCell>{formatNumberToDisplay(yearData.total_actual_production, { suffix: uom })}</TableCell>
                    <TableCell>
                      <VarianceDisplay
                        actual={yearData.total_actual_production}
                        projected={yearData.total_projected_production}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// Commodity breakdown table
const CommodityProductionTable = ({ data }: { data: TopLevelResult }) => {

  const [expandedUoms, setExpandedUoms] = useState<Record<string, boolean>>({});
  const [expandedYears, setExpandedYears] = useState<Record<string, boolean>>({});

  if (!data?.summaries?.uom_summaries) return null;

  const toggleUom = (uom: string) => {
    setExpandedUoms(prev => ({
      ...prev,
      [uom]: !prev[uom]
    }));
  };

  const toggleYear = (uomYear: string) => {
    setExpandedYears(prev => ({
      ...prev,
      [uomYear]: !prev[uomYear]
    }));
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Commodity Production Breakdown</CardTitle>
            <CardDescription>Production details by commodity, year, and UOM</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">UOM / Year / Commodity</TableHead>
              <TableHead>Projected Production</TableHead>
              <TableHead>Actual Production</TableHead>
              <TableHead>Variance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(data.summaries.uom_summaries).map(([uom, summary]) => (
              <Fragment key={uom}>
                <TableRow className="bg-muted/50 cursor-pointer" onClick={() => toggleUom(uom)}>
                  <TableCell className="font-medium flex items-center">
                    {expandedUoms[uom] ? <ChevronUp className="h-4 w-4 mr-2" /> : <ChevronDown className="h-4 w-4 mr-2" />}
                    {uom}
                  </TableCell>
                  <TableCell>{formatNumberToDisplay(summary.total_projected_production, { suffix: uom })}</TableCell>
                  <TableCell>{formatNumberToDisplay(summary.total_actual_production, { suffix: uom })}</TableCell>
                  <TableCell>
                    <VarianceDisplay
                      actual={summary.total_actual_production}
                      projected={summary.total_projected_production}
                    />
                  </TableCell>
                </TableRow>

                {expandedUoms[uom] && Object.entries(summary.by_year).map(([year, yearData]) => (
                  <Fragment key={`${uom}-${year}`}>
                    <TableRow
                      className="bg-background/70 cursor-pointer hover:bg-muted/20"
                      onClick={() => toggleYear(`${uom}-${year}`)}
                    >
                      <TableCell className="pl-8 font-medium flex items-center">
                        {expandedYears[`${uom}-${year}`] ? <ChevronUp className="h-4 w-4 mr-2" /> : <ChevronDown className="h-4 w-4 mr-2" />}
                        {year}
                      </TableCell>
                      <TableCell>{formatNumberToDisplay(yearData.total_projected_production, { suffix: uom })}</TableCell>
                      <TableCell>{formatNumberToDisplay(yearData.total_actual_production, { suffix: uom })}</TableCell>
                      <TableCell>
                        <VarianceDisplay
                          actual={yearData.total_actual_production}
                          projected={yearData.total_projected_production}
                        />
                      </TableCell>
                    </TableRow>

                    {expandedYears[`${uom}-${year}`] && Object.entries(yearData.by_commodity).map(([commodity, commodityData]) => (
                      <TableRow key={`${uom}-${year}-${commodity}`} className="bg-background/20">
                        <TableCell className="pl-12">{commodity}</TableCell>
                        <TableCell>{formatNumberToDisplay(commodityData.total_projected_production, { suffix: uom })}</TableCell>
                        <TableCell>{formatNumberToDisplay(commodityData.total_actual_production, { suffix: uom })}</TableCell>
                        <TableCell>
                          <VarianceDisplay
                            actual={commodityData.total_actual_production}
                            projected={commodityData.total_projected_production}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// Customer Summary Table
const CustomerSummaryTable = ({ data }: { data: TopLevelResult }) => {
  const [expandedCustomers, setExpandedCustomers] = useState<Record<string, boolean>>({});
  if (!data?.summaries?.customers) return null;

  const toggleCustomer = (customerId: string) => {
    setExpandedCustomers(prev => ({
      ...prev,
      [customerId]: !prev[customerId]
    }));
  };

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Customer Production Summary</CardTitle>
            <CardDescription>Production totals by customer and UOM</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[250px]">Customer / UOM</TableHead>
              <TableHead>Projected Production</TableHead>
              <TableHead>Actual Production</TableHead>
              <TableHead>Variance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(data.summaries.customers).map(([customerId, customer]) => (
              <Fragment key={customerId}>
                <TableRow className="bg-muted/50 cursor-pointer" onClick={() => toggleCustomer(customerId)}>
                  <TableCell className="font-medium flex items-center">
                    {expandedCustomers[customerId] ?
                      <ChevronUp className="h-4 w-4 mr-2" /> :
                      <ChevronDown className="h-4 w-4 mr-2" />
                    }
                    {customer.customer_name}
                  </TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>—</TableCell>
                </TableRow>

                {expandedCustomers[customerId] && Object.entries(customer.uom_summaries).map(([uom, summary]) => (
                  <TableRow key={`${customerId}-${uom}`} className="bg-background/50">
                    <TableCell className="pl-8">{uom}</TableCell>
                    <TableCell>{formatNumberToDisplay(summary.total_projected_production, { suffix: uom })}</TableCell>
                    <TableCell>{formatNumberToDisplay(summary.total_actual_production, { suffix: uom })}</TableCell>
                    <TableCell>
                      <VarianceDisplay
                        actual={summary.total_actual_production}
                        projected={summary.total_projected_production}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// Main component that combines all tables
export function RiskManagementSummaryTables({
  data,
  isLoading
}: RiskManagementSummaryTablesProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading summary data...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="mb-6">
        <CardContent className="flex items-center justify-center h-40">
          <p className="text-muted-foreground">No data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <TotalProductionSummaryCard data={data} />
      <YearlyProductionTable data={data} />
      <CommodityProductionTable data={data} />
      <CustomerSummaryTable data={data} />
    </div>
  );
}

export default RiskManagementSummaryTables;
